import { AnalyticsUserEvent } from '@fruitful/types';
import addDays from 'date-fns/addDays';
import addHours from 'date-fns/addHours';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';

import type { GuideWithAvailability } from '../../guidance/types/Guide';
import { track } from '../../logger/Logger';

const QUERY_START_HOUR_OFFSET = 1; // get availability starting 1 hr from now

/** Local date/time for analytics (matches "YYYY-MM-DD hh:mm" style; 24h clock). */
const QUERY_WINDOW_DATETIME_FORMAT = 'yyyy-MM-dd HH:mm';

export type Direction = 'prev' | 'next';

/** Aggregate stats per guide for analytics (sessions = sessionAvailableTimes length). */
const getAvailabilitySessionStats = (
    guides: GuideWithAvailability[],
    membershipGuideId?: string,
): {
    numberOfAvailableGuides: number;
    avgSessionsPerAvailableGuide: number;
    medianSessionsPerAvailableGuide: number;
    maxSessionsPerAvailableGuide: number;
    totalSessionsAvailableGuides: number;
    totalSessionsMembershipGuide: number;
} | null => {
    if (guides.length === 0) {
        return null;
    }

    const availableGuides = guides.filter(
        (gwa) => (gwa.sessionAvailableTimes ?? []).length > 0,
    );
    const numberOfAvailableGuides = availableGuides.length;
    const guideSessionsCounts = availableGuides.map(
        (g) => g.sessionAvailableTimes?.length ?? 0,
    );
    const totalSessionsAvailableGuides = guideSessionsCounts.reduce(
        (a, b) => a + b,
        0,
    );
    const maxSessionsPerAvailableGuide = Math.max(...guideSessionsCounts);
    const avgSessionsPerAvailableGuide =
        totalSessionsAvailableGuides / numberOfAvailableGuides;

    const sorted = [...guideSessionsCounts].sort((a, b) => a - b);
    const n = sorted.length;
    const medianSessionsPerAvailableGuide =
        ((sorted[Math.floor((n - 1) / 2)] ?? 0) +
            (sorted[Math.ceil((n - 1) / 2)] ?? 0)) /
        2;

    const membershipGuide = guides.find((g) => g.id === membershipGuideId);
    const totalSessionsMembershipGuide =
        membershipGuide?.sessionAvailableTimes?.length ?? 0;

    return {
        numberOfAvailableGuides,
        avgSessionsPerAvailableGuide:
            Math.round(avgSessionsPerAvailableGuide * 100) / 100,
        medianSessionsPerAvailableGuide:
            Math.round(medianSessionsPerAvailableGuide * 100) / 100,
        maxSessionsPerAvailableGuide,
        totalSessionsAvailableGuides,
        totalSessionsMembershipGuide,
    };
};

interface FirstSessionAvailabilityChunkQuery {
    guidesWithAvailability: GuideWithAvailability[] | null;
    isLoading: boolean;
    isPending: boolean;
    isFetching: boolean;
    isError: boolean;
}
export interface AvailabilityQueryTimeRange {
    start: Date;
    end: Date;
}
interface AvailabilityQueryTimeRanges {
    dateRange1: AvailabilityQueryTimeRange;
    dateRange2: AvailabilityQueryTimeRange;
    dateRange3: AvailabilityQueryTimeRange;
}

/** Fires once per chunk when availability has loaded and stats are computable. */
export const logAvailabilityStats = ({
    chunkIndex,
    query,
    timeRange,
    membershipGuideId,
    enabled = true,
}: {
    chunkIndex: 1 | 2 | 3;
    query: FirstSessionAvailabilityChunkQuery;
    timeRange: AvailabilityQueryTimeRange;
    membershipGuideId: string | undefined;
    enabled?: boolean;
}): boolean => {
    if (
        !enabled ||
        query.guidesWithAvailability == null ||
        query.isPending ||
        query.isError ||
        query.guidesWithAvailability.length === 0
    ) {
        return false;
    }

    const stats = getAvailabilitySessionStats(
        query.guidesWithAvailability,
        membershipGuideId,
    );
    if (stats == null) {
        return false;
    }

    track(AnalyticsUserEvent.ONBOARDING_BOOK_1ST_SESSION_AVAILABILITY_DATA, {
        chunkIndex,
        queryWindowStart: format(timeRange.start, QUERY_WINDOW_DATETIME_FORMAT),
        queryWindowEnd: format(timeRange.end, QUERY_WINDOW_DATETIME_FORMAT),
        ...stats,
    });

    return true;
};

export const getAvailabilityQueryTimeRanges = (
    initDate: Date,
    rangeSize: number,
): AvailabilityQueryTimeRanges => {
    const range1StartDate = addHours(initDate, QUERY_START_HOUR_OFFSET);
    const range1EndDate = addDays(endOfDay(range1StartDate), rangeSize - 1);
    const dateRange1 = { start: range1StartDate, end: range1EndDate };
    const dateRange2 = {
        start: addDays(startOfDay(dateRange1.start), rangeSize),
        end: addDays(dateRange1.end, rangeSize),
    };
    const dateRange3 = {
        start: addDays(startOfDay(dateRange2.start), rangeSize),
        end: addDays(dateRange2.end, rangeSize),
    };
    return { dateRange1, dateRange2, dateRange3 };
};

interface AvailabilityForDay {
    day: Date;
    availability: Array<GuideWithAvailability | undefined>;
}

export const getEarliestAvailabilityDay = (
    guides: Array<GuideWithAvailability | undefined>,
): Date | undefined => {
    const earliest = minBy(
        guides.flatMap((guide) => guide?.sessionAvailableTimes),
        (slot) => slot?.getTime(),
    );
    return earliest ? startOfDay(earliest) : undefined;
};

export const getLatestAvailabilityDay = (
    guides: Array<GuideWithAvailability | undefined>,
): Date | undefined => {
    const latest = maxBy(
        guides.flatMap((guide) => guide?.sessionAvailableTimes),
        (slot) => slot?.getTime(),
    );
    return latest ? startOfDay(latest) : undefined;
};

const getDedupedSortedTimes = (allTimes: Date[]): Date[] => {
    const timeMap = new Map<number, Date>();
    allTimes.forEach((time) => {
        timeMap.set(time.getTime(), time);
    });
    return [...timeMap.values()].sort((a, b) => a.getTime() - b.getTime());
};

/** Merges API responses: same guide ids get concatenated, deduped slot times. */
export const mergeGuidesWithAvailability = (
    ...chunks: GuideWithAvailability[][]
): GuideWithAvailability[] => {
    const guidesMap = new Map<
        GuideWithAvailability['id'],
        GuideWithAvailability
    >();

    const upsert = (guide: GuideWithAvailability) => {
        const existing = guidesMap.get(guide.id);
        const allTimes = [
            ...(existing?.sessionAvailableTimes ?? []),
            ...(guide.sessionAvailableTimes ?? []),
        ];
        const uniqueSortedTimes = getDedupedSortedTimes(allTimes);
        guidesMap.set(guide.id, {
            ...guide,
            sessionAvailableTimes: uniqueSortedTimes,
        });
    };

    chunks.forEach((chunk) => {
        chunk.forEach(upsert);
    });

    return [...guidesMap.values()].sort((a, b) => {
        const aTimes = a.sessionAvailableTimes ?? [];
        const bTimes = b.sessionAvailableTimes ?? [];
        return bTimes.length - aTimes.length;
    });
};

/**
 * Guides with slots on `day` only. Overwrites `sessionAvailableTimes` so callers
 * must not read the full merged list from the spread (see onboarding cards).
 */
const getAvailabilityForDay = (
    guides: Array<GuideWithAvailability | undefined>,
    day: Date,
): Array<GuideWithAvailability | undefined> => {
    const dayGuidesWithAvailability = guides
        .map((guideWithAvailability) => {
            if (!guideWithAvailability) {
                return undefined;
            }
            return {
                ...guideWithAvailability,
                sessionAvailableTimes:
                    guideWithAvailability.sessionAvailableTimes?.filter(
                        (slot) => isSameDay(slot, day),
                    ),
            };
        })
        .filter((gwa) => (gwa?.sessionAvailableTimes ?? []).length > 0);

    if (dayGuidesWithAvailability.length <= 5) {
        return dayGuidesWithAvailability;
    }

    const dayGuidesWithSufficientAvailability =
        dayGuidesWithAvailability.filter(
            (gwa) => (gwa?.sessionAvailableTimes ?? []).length >= 3,
        );

    if (dayGuidesWithSufficientAvailability.length >= 5) {
        return dayGuidesWithSufficientAvailability;
    }

    return dayGuidesWithAvailability;
};

export const getNonEmptyAvailabilityPrevOrNext = ({
    guides,
    selectedDay,
    direction,
    maxIterations,
    currentIteration = 0,
    checkSelectedDay = false,
    stopAtDay,
}: {
    guides: Array<GuideWithAvailability | undefined>;
    selectedDay: Date;
    direction: Direction;
    maxIterations: number;
    currentIteration?: number;
    checkSelectedDay?: boolean;
    /**
     * Optional navigation bound, so iterations are capped at maxIterations or this date (whichever happens first)
     */
    stopAtDay?: Date;
}): AvailabilityForDay => {
    if (currentIteration >= maxIterations) {
        return {
            day: selectedDay,
            availability: [],
        } satisfies AvailabilityForDay;
    }

    let day = selectedDay;
    let dayAvailability: Array<GuideWithAvailability | undefined> = [];
    let anyGuideHasAvailabilityThisDay = false;

    if (!checkSelectedDay) {
        day = direction === 'prev' ? subDays(day, 1) : addDays(day, 1);
    }
    if (direction === 'next' && stopAtDay != null && isAfter(day, stopAtDay)) {
        return {
            day: selectedDay,
            availability: [],
        } satisfies AvailabilityForDay;
    }
    if (
        direction === 'prev' &&
        stopAtDay != null &&
        isBefore(startOfDay(day), startOfDay(stopAtDay))
    ) {
        return {
            day: selectedDay,
            availability: [],
        } satisfies AvailabilityForDay;
    }
    dayAvailability = getAvailabilityForDay(guides, day);
    anyGuideHasAvailabilityThisDay = dayAvailability.length > 0;

    if (anyGuideHasAvailabilityThisDay) {
        return {
            day,
            availability: dayAvailability,
        } satisfies AvailabilityForDay;
    }

    return getNonEmptyAvailabilityPrevOrNext({
        guides,
        selectedDay: day,
        direction,
        maxIterations,
        currentIteration: currentIteration + 1,
        checkSelectedDay: false,
        stopAtDay,
    });
};
